/* eslint-disable no-nested-ternary */

import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardText,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { Form, Field, Button, CardTitle, PseudoStates, useFeatures } from '@cordata/buffet';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { camelCase, find, sortBy, reduce, capitalize, lowerCase } from 'lodash';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { extInstance as base, useExt } from '~/utils';

const transformLabel = label => ({
  label,
  value: camelCase(label),
});

const primaryPhoneNumber = ['Mobile Number', 'Home Number', 'Work Number'];

const primaryPhoneNumbers = primaryPhoneNumber.map(transformLabel);

const fromProviderKeys = [
  'fromProviderRoleUid',
  'fromProviderPhone',
  'fromProviderEmail',
  'fromProviderFirstName',
  'fromProviderLastName',
];

const ExternalReferral = () => {
  const { externalActionInstance, type } = useParams();
  const [data, setData] = useState({});
  const [schema, setSchema] = useState({ required: [] });
  const [status, setStatus] = useState('loading');
  const [initialValues, setIntialValues] = useState({});
  const [t] = useTranslation();
  const { eai, postIt } = useExt();
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('success');
  const features = useFeatures();

  const toastId = 'validate-this';

  const translateValue = value => ({
    label: capitalize(t(value)),
    value: capitalize(value),
  });

  const requestedBy = ['patient', 'provider'].map(translateValue);

  const checkRequired = field => Object.keys(schema.required).includes(field);

  const validateThis = errors => {
    const msg = 'Invalid Form';

    if (errors) {
      toast.update(toastId, { type: toast.TYPE.ERROR, render: msg });
    }

    if (!toast.isActive(toastId) && errors) {
      toast.error(msg, { toastId });
    }
  };

  useEffect(() => {
    base
      .get(`ext/${externalActionInstance}`)
      .then(r => r.data)
      .then(r => {
        setData({
          ...reduce(
            r.formData,
            (a, v, k) => ({
              ...a,
              [k]: sortBy(v, ['defaultSortOrder', 'name', 'label']),
            }),
            {},
          ),
          fromProviderUid: [
            {
              id: 'new',
              name: "I don't see me, add me",
            },
            ...sortBy(r.formData.toProviderUid, ['defaultSortOrder', 'name']),
          ],
        });
        setSchema({
          ...r.payload,
          required: r.payload.required.reduce(
            (a, m) => ({
              ...a,
              [camelCase(m)]: Yup.string()
                .nullable()
                .required(),
            }),
            {
              requestedBy: Yup.mixed().required(),
              fromProviderUid: Yup.mixed().when('requestedBy', (v, s) =>
                (v === 'Provider' ? s.required() : s.notRequired())),
              ...fromProviderKeys.reduce(
                (a, m) => ({
                  ...a,
                  [m]: Yup.mixed().when('fromProviderUid', (v, s) =>
                    (v && v.id === 'new' ? s.required() : s.notRequired())),
                }),
                {},
              ),
              ...primaryPhoneNumber.reduce(
                (a, m) => ({
                  ...a,
                  [camelCase(m)]: Yup.mixed().when(
                    'primaryPhoneNumber',
                    (v, s) =>
                      (v === camelCase(m) ? s.required() : s.notRequired()),
                  ),
                }),
                {},
              ),
            },
          ),
        });
        setIntialValues({
          ...reduce(
            r.payload.properties,
            (a, v, k) => ({
              ...a,
              [camelCase(k)]: '',
            }),
            {},
          ),
          policyHolder: true,
          policyType: true,
          addressTypeUid: find(r.formData.addressTypeUid, [
            'name',
            'Primary residence',
          ]),
          isPrimary: true,
          referralDate: new Date(),
          referralMethod: 'Online',
          requestedBy: undefined,
          primaryPhoneNumber: undefined,
          fromProviderPCP: undefined,
        });

        setStatus('success');
      })
      .catch(e => {
        setData(e);
        setStatus('error');
      });
  }, []);

  return status === 'success' ? (
    <Form
      displayName="ExternalReferral"
      validateOnChange
      validateOnBlur
      initialValues={initialValues}
      validationSchema={Yup.object().shape(schema.required)}
      enableReinitialize
      onSubmit={(values, { setErrors, setSubmitting }) => {
        if (values.fromProviderUid && values.fromProviderUid.id === 'new') {
            // eslint-disable-next-line no-param-reassign
            values.fromProviderUid = undefined;
           }

        postIt(values)
          .then(({ data: { message } }) => {
            setSuccessMessage(message);
            setSuccessOpen(true);
          })
          .catch(({ message }) => {
            setErrors({ server: message });

            toast.error(message);
          })
          .then(() => setSubmitting(false));
      }}
    >
      {({
        isSubmitting,
        values,
        isValidating,
        submitCount,
        isValid,
        errors,
      }) => (
        <Card>
          {submitCount >= 1 && isSubmitting && isValidating && validateThis()}
          {submitCount >= 1 && isSubmitting && !isValid && validateThis(errors)}

          <CardBody className="p-0">
            <CardText className="p-3">
              Thank you for contacting {eai.divisionName} a division of{' '}
              {eai.orgName}. Please fill out as much information as you can in
              the form below. Items with the red asterisk (
              <span className="text-danger">*</span>) are required. A member of
              our team will review your referral and contact you shortly.
            </CardText>

            <fieldset className="form-section">
              <Field
                name="requestedBy"
                label="Who am I?"
                type="radio-group"
                options={requestedBy}
                inline
                required={checkRequired('requestedBy')}
              />
            </fieldset>

            {values.requestedBy === 'Provider' && (
              <fieldset className="form-section">
                <CardTitle>Contact Information</CardTitle>

                <CardText>
                  If you have previously referred a {t('patient')} to us, please
                  choose your name from the list below. Otherwise, enter the
                  information so we can follow-up with any questions.
                </CardText>

                <Field
                  name="fromProviderUid"
                  label="From Provider"
                  type="select"
                  options={data.fromProviderUid}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.name}
                  required
                />

                {values.fromProviderUid && values.fromProviderUid.id === 'new' && (
                  <>
                    <Row form>
                      <Col sm>
                        <Field name="fromProviderFirstName" required />
                      </Col>

                      <Col sm>
                        <Field name="fromProviderLastName" required />
                      </Col>
                    </Row>

                    <Row form>
                      <Col sm>
                        <Field
                          name="fromProviderRoleUid"
                          label="From Provider Role"
                          type="select"
                          options={data.providerRoleUid}
                          getOptionValue={o => o.id}
                          getOptionLabel={o => o.label}
                          required
                        />
                      </Col>

                      <Col sm>
                        <Field name="fromProviderPhone" type="tel" required />
                      </Col>

                      <Col sm>
                        <Field name="fromProviderEmail" type="email" required />
                      </Col>
                    </Row>
                  </>
                )}

                <Field
                  name="fromProviderPCP"
                  label={`Check the box below if you are the ${t('patient')}'s family or primary care physician`}
                  type="checkbox"
                  placeholder="Yes"
                  required={checkRequired('fromProviderPCP')}
                />
              </fieldset>
            )}

            <fieldset className="form-section" disabled={!values.requestedBy}>
              <CardTitle>{capitalize(t('patient'))} Information</CardTitle>

              <Row form>
                <Col sm>
                  <Field
                    name="firstName"
                    required={checkRequired('firstName')}
                  />
                </Col>

                <Col sm>
                  <Field name="lastName" required={checkRequired('lastName')} />
                </Col>
              </Row>

              <Field
                name="displayName"
                label="Preferred Name"
                required={checkRequired('displayName')}
              />

              <Row form>
                <Col sm>
                  <Field
                    name="birthDate"
                    type="date"
                    format="MM/dd/yyyy"
                    required={checkRequired('birthDate')}
                    disabled={!values.requestedBy}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="genderUid"
                    label="Gender"
                    type="select"
                    options={data.genderUid}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.label}
                    required={checkRequired('genderUid')}
                    disabled={!values.requestedBy}
                  />
                </Col>
                <Col sm>
                  <Field
                    name="genderIdentityUid"
                    label="Gender Identity"
                    type="select"
                    options={data.genderIdentityUid}
                    getOptionValue={o => o.genderIdentityUid}
                    getOptionLabel={o => o.genderIdentityLabel}
                    required={checkRequired('genderUid')}
                    disabled={!values.requestedBy}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="raceUid"
                    label="Race"
                    type="select"
                    options={data.raceUid}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.label}
                    required={checkRequired('raceUid')}
                    disabled={!values.requestedBy}
                  />
                </Col>
                {features.hideEthnicity && (
                <Col sm>
                  <Field
                    name="ethnicityUid"
                    label="Ethnicity"
                    type="select"
                    options={data.ethnicityUid}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.label}
                    required={checkRequired('ethnicityUid')}
                    disabled={!values.requestedBy}
                  />
                </Col>
                )}
              </Row>
            </fieldset>

            <fieldset className="form-section" disabled={!values.requestedBy}>
              <CardTitle>
                {capitalize(t('patient'))} Contact Information
              </CardTitle>

              <Field
                name="primaryPhoneNumber"
                type="radio-group"
                options={primaryPhoneNumbers}
                inline
              />

              <Row form>
                <Col sm>
                  <Field
                    name="mobileNumber"
                    type="tel"
                    required={values.primaryPhoneNumber === 'mobileNumber'}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="homeNumber"
                    type="tel"
                    required={values.primaryPhoneNumber === 'homeNumber'}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="workNumber"
                    type="tel"
                    required={values.primaryPhoneNumber === 'workNumber'}
                  />
                </Col>
              </Row>

              <Field
                name="emailAddress"
                type="email"
                required={checkRequired('emailAddress')}
              />
            </fieldset>

            <fieldset className="form-section" disabled={!values.requestedBy}>
              <CardTitle>Home Address</CardTitle>

              <Row form>
                <Col sm>
                  <Field
                    name="addr1"
                    label="Address 1"
                    required={checkRequired('addr1')}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="addr2"
                    label="Address 2"
                    required={checkRequired('addr2')}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="postCode"
                    label="Zip Code"
                    required={checkRequired('postCode')}
                  />
                </Col>

                <Col sm>
                  <Field name="city" required={checkRequired('city')} />
                </Col>

                <Col sm>
                  <Field
                    name="countryDivisionUid"
                    label="State"
                    type="select"
                    options={data.countryDivisionUid}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.label}
                    required={checkRequired('countryDivisionUid')}
                    disabled={!values.requestedBy}
                  />
                </Col>

                <Col sm>
                  <Field name="county" required={checkRequired('county')} />
                </Col>
              </Row>
            </fieldset>

            <fieldset className="form-section" disabled={!values.requestedBy}>
              <CardTitle>Primary Insurance</CardTitle>

              <Row form>
                <Col sm>
                  <Field
                    name="policyHolderName"
                    required={checkRequired('policyHolderName')}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="ssn"
                    label="Social Security Number"
                    required={checkRequired('ssn')}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="policyHolderBirthDate"
                    type="date"
                    format="MM/dd/yyyy"
                    required={checkRequired('policyHolderBirthDate')}
                    disabled={!values.requestedBy}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="carrierUid"
                    label="Insurance Company / Carrier"
                    type="select"
                    options={data.carrierUid}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.name}
                    required={checkRequired('carrierUid')}
                    disabled={!values.requestedBy}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="insuranceCompanyPhone"
                    type="tel"
                    required={checkRequired('insuranceCompanyPhone')}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm>
                  <Field
                    name="policyNumber"
                    required={checkRequired('policyNumber')}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="groupNumber"
                    required={checkRequired('groupNumber')}
                  />
                </Col>

                <Col sm>
                  <Field
                    name="effectiveDate"
                    type="date"
                    format="MM/dd/yyyy"
                    required={checkRequired('effectiveDate')}
                    disabled={!values.requestedBy}
                  />
                </Col>
              </Row>
            </fieldset>

            <fieldset className="form-section" disabled={!values.requestedBy}>
              <CardTitle>Referral Details</CardTitle>

              <CardText>
                Please identify relevant information about this referral so we
                can better process your request.
              </CardText>

              <Field
                name="reasonUid"
                label="Reason"
                type="select"
                options={data.reasonUid}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.label}
                required={checkRequired('reasonUid')}
                disabled={!values.requestedBy}
              />

              <Field
                name="note"
                label={`Enter any other pertinent information that will enable us to better help this ${t('patient')}`}
                placeholder=" "
                type="textarea"
                required={checkRequired('note')}
              />

              <Field
                name="diagnosisUid"
                label="Diagnosis"
                type="select"
                options={data.diagnosisUid}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.label}
                required={checkRequired('diagnosisUid')}
                disabled={!values.requestedBy}
              />

              <Field
                name="advertisementUid"
                label="How did you hear about us?"
                type="select"
                options={data.advertisementUid}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.name}
                required={checkRequired('advertisementUid')}
                disabled={!values.requestedBy}
              />

              <Field
                name="toProviderUid"
                label="Requested Specialist"
                type="select"
                options={data.toProviderUid}
                getOptionValue={o => o.id}
                getOptionLabel={o => o.name}
                required={checkRequired('toProviderUid')}
                disabled={!values.requestedBy}
              />

              <Field
                name="referralDate"
                type="date"
                format="MM/dd/yyyy"
                required={checkRequired('referralDate')}
                disabled
              />
            </fieldset>
          </CardBody>

          <CardFooter className="d-flex">
            <div className="ml-auto">
              <Button type="submit" disabled={isSubmitting}>
                Submit{isSubmitting && 'ing'}
              </Button>
            </div>
          </CardFooter>

          <Modal isOpen={successOpen} backdrop="static">
            <ModalHeader>{successMessage}</ModalHeader>

            <ModalBody>
              Thanks for your referral. You can now safely close your browser
              tab.
            </ModalBody>

            <ModalFooter>
              <Button color="primary" onClick={() => window.location.reload()}>
                <FontAwesomeIcon icon="undo" />
                <span className="d-none d-md-inline ml-2">
                  Start another {lowerCase(type)}
                </span>
              </Button>
            </ModalFooter>
          </Modal>
        </Card>
      )}
    </Form>
  ) : (
    <PseudoStates status={status} />
  );
};

export default ExternalReferral;
